import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Flapinaction from '../ui/Flapinactions';
import Testimonial1 from '../ui/Testimonial1';
import TrustedBy from '../ui/TrustedBy';
import About from '../ui/About';
import Footer from '../ui/Footer';

/* ─────────────────────────────────────────────────────────────────────────────
   Home.js  —  Flap Platform landing page
   Change vs previous: hero stats fetched from /api/analytics/public-stats
   instead of hardcoded "10K+", "98%", "4x"
───────────────────────────────────────────────────────────────────────────── */

// Same API_BASE pattern as Flapinaction.js — already ends with /api
const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
@import url('https://api.fontshare.com/v2/css?f[]=clash-display@400,500,600,700&display=swap');

:root {
  --navy:    #06112b;
  --navy2:   #0d1f45;
  --navy3:   #112359;
  --blue:    #1c73ba;
  --blue2:   #2b8de0;
  --blue3:   #5aaef7;
  --gold:    #f5a623;
  --gold2:   #ffc94d;
  --white:   #ffffff;
  --offwhite:#f0f5ff;
  --muted:   #94a3b8;
  --text:    #1e293b;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.flap-home {
  font-family: 'DM Sans', sans-serif;
  background: var(--white);
  color: var(--text);
  overflow-x: hidden;
}

/* ── NAV ─────────────────────────────────────────────────────────────────── */
.flap-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 100;
  padding: 0 5%;
  height: 68px;
  display: flex; align-items: center; justify-content: space-between;
  transition: background 0.3s, box-shadow 0.3s;
}
.flap-nav.scrolled {
  background: rgba(6,17,43,0.95);
  backdrop-filter: blur(20px);
  box-shadow: 0 1px 0 rgba(255,255,255,0.06);
}
.flap-nav-logo {
  font-family: 'Clash Display', sans-serif;
  font-weight: 700;
  font-size: 1.6rem;
  color: #fff;
  text-decoration: none;
  letter-spacing: -0.5px;
}
.flap-nav-logo span { color: var(--gold); }
.flap-nav-links {
  display: flex; align-items: center; gap: 1.5rem;
}
.flap-nav-links a {
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: color 0.2s;
}
.flap-nav-links a:hover { color: #fff; }
.flap-nav-cta {
  background: var(--gold);
  color: var(--navy) !important;
  padding: 0.45rem 1.2rem !important;
  border-radius: 6px;
  font-weight: 700 !important;
  transition: background 0.2s !important;
}
.flap-nav-cta:hover { background: var(--gold2) !important; }
@media (max-width: 640px) {
  .flap-nav-links .hide-mobile { display: none; }
}

/* ── HERO ─────────────────────────────────────────────────────────────────── */
.flap-hero {
  min-height: 100vh;
  background: linear-gradient(145deg, var(--navy) 0%, var(--navy3) 55%, #1a3570 100%);
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  padding: 100px 5% 60px;
  position: relative;
  overflow: hidden;
  gap: 40px;
}
.flap-hero::before {
  content: '';
  position: absolute; inset: 0;
  background-image:
    linear-gradient(rgba(28,115,186,0.07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(28,115,186,0.07) 1px, transparent 1px);
  background-size: 52px 52px;
  pointer-events: none;
}
.flap-hero::after {
  content: '';
  position: absolute;
  width: 600px; height: 600px;
  background: radial-gradient(circle, rgba(28,115,186,0.18) 0%, transparent 70%);
  top: -100px; right: -100px;
  pointer-events: none;
}
.hero-glow2 {
  position: absolute;
  width: 400px; height: 400px;
  background: radial-gradient(circle, rgba(245,166,35,0.1) 0%, transparent 70%);
  bottom: -80px; left: 10%;
  pointer-events: none;
}

.flap-hero-text { position: relative; z-index: 2; }

.flap-hero-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(245,166,35,0.12);
  border: 1px solid rgba(245,166,35,0.3);
  border-radius: 99px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--gold);
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 24px;
  animation: fadeUp 0.7s ease both;
}
.eyebrow-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--gold);
  animation: pulse-dot 2s infinite;
}

.flap-hero h1 {
  font-family: 'Clash Display', sans-serif;
  font-size: clamp(2.4rem, 4.5vw + 0.5rem, 4rem);
  font-weight: 700;
  line-height: 1.08;
  color: #fff;
  margin-bottom: 22px;
  animation: fadeUp 0.7s 0.1s ease both;
}
.flap-hero h1 em { font-style: normal; color: var(--blue3); }
.flap-hero h1 strong {
  font-style: normal;
  background: linear-gradient(90deg, var(--gold), var(--gold2));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.flap-hero-sub {
  font-size: clamp(0.95rem, 1.5vw, 1.15rem);
  color: rgba(255,255,255,0.65);
  line-height: 1.7;
  max-width: 520px;
  margin-bottom: 36px;
  animation: fadeUp 0.7s 0.2s ease both;
}

.hero-btns {
  display: flex; gap: 14px; flex-wrap: wrap;
  animation: fadeUp 0.7s 0.3s ease both;
}
.hero-btn-primary {
  display: inline-flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg, var(--blue), var(--blue2));
  color: #fff;
  padding: 0.75rem 1.8rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 6px 24px rgba(28,115,186,0.4);
}
.hero-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(28,115,186,0.5); }
.hero-btn-secondary {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,0.08);
  color: #fff;
  padding: 0.75rem 1.8rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  border: 1px solid rgba(255,255,255,0.18);
  transition: background 0.2s, transform 0.2s;
}
.hero-btn-secondary:hover { background: rgba(255,255,255,0.14); transform: translateY(-2px); }

/* ── HERO STATS — live numbers ─────────────────────────────────────────────── */
.hero-stats {
  display: flex; gap: 32px; margin-top: 48px;
  animation: fadeUp 0.7s 0.4s ease both;
}
.hero-stat-num {
  font-family: 'Clash Display', sans-serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: #fff;
  line-height: 1;
  min-width: 56px; /* prevent layout shift while loading */
}
.hero-stat-num span { color: var(--gold); }
.hero-stat-num.loading {
  background: rgba(255,255,255,0.08);
  border-radius: 6px;
  color: transparent;
  animation: shimmerStat 1.4s infinite;
}
@keyframes shimmerStat {
  0%,100% { opacity: 0.4; } 50% { opacity: 0.8; }
}
.hero-stat-label {
  font-size: 0.75rem;
  color: var(--muted);
  margin-top: 3px;
  text-transform: uppercase;
  letter-spacing: 1px;
}

/* ── 3D NFC CARD ─────────────────────────────────────────────────────────── */
.hero-visual {
  position: relative; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  animation: fadeUp 0.8s 0.2s ease both;
}
.card-scene { perspective: 900px; width: 340px; height: 220px; }
.card-3d {
  width: 100%; height: 100%;
  position: relative;
  transform-style: preserve-3d;
  animation: cardFloat 5s ease-in-out infinite, cardTilt 8s ease-in-out infinite;
  filter: drop-shadow(0 30px 60px rgba(0,0,0,0.5));
}
.card-face {
  position: absolute; inset: 0;
  border-radius: 18px;
  backface-visibility: hidden;
  background: linear-gradient(135deg, #1c3a6b 0%, #2563a0 50%, #1c73ba 100%);
  border: 1px solid rgba(255,255,255,0.15);
  padding: 24px 28px;
  overflow: hidden;
}
.card-face::before {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(105deg, transparent 40%, rgba(255,255,255,0.04) 45%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 55%, transparent 60%);
  animation: shimmer 3s ease-in-out infinite;
}
.card-face::after {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: radial-gradient(ellipse at 30% 40%, rgba(255,255,255,0.07) 0%, transparent 60%);
}
.card-logo { font-family: 'Clash Display', sans-serif; font-size: 1.4rem; font-weight: 700; color: #fff; letter-spacing: -0.5px; position: relative; z-index: 1; }
.card-logo span { color: var(--gold); }
.card-chip { position: absolute; top: 26px; right: 28px; width: 38px; height: 30px; background: linear-gradient(135deg, #d4a843, #f5c842, #c49030); border-radius: 5px; z-index: 1; }
.card-chip::before { content: ''; position: absolute; inset: 5px; border: 1.5px solid rgba(0,0,0,0.15); border-radius: 2px; }
.card-nfc { position: absolute; bottom: 22px; right: 26px; z-index: 1; }
.card-nfc-rings { width: 32px; height: 32px; position: relative; }
.card-nfc-rings::before,.card-nfc-rings::after,.card-nfc-rings span { content: ''; position: absolute; border: 2px solid rgba(255,255,255,0.5); border-radius: 50%; left: 50%; top: 50%; transform: translate(-50%, -50%); }
.card-nfc-rings::before { width: 10px; height: 10px; }
.card-nfc-rings::after  { width: 20px; height: 20px; opacity: 0.7; }
.card-nfc-rings span    { display:block; width: 30px; height: 30px; opacity: 0.4; }
.card-name { position: absolute; bottom: 26px; left: 28px; font-family: 'Clash Display', sans-serif; font-size: 1.05rem; font-weight: 600; color: rgba(255,255,255,0.9); letter-spacing: 0.5px; z-index: 1; }
.card-detail { font-size: 0.7rem; color: rgba(255,255,255,0.45); margin-top: 2px; letter-spacing: 1px; text-transform: uppercase; }
.card-accessory { position: absolute; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.1); border-radius: 12px; padding: 8px 12px; font-size: 0.72rem; color: rgba(255,255,255,0.7); backdrop-filter: blur(8px); white-space: nowrap; }
.ca-1 { top: -20px; right: -10px; animation: float1 4s ease-in-out infinite; }
.ca-2 { bottom: -18px; left: -10px; animation: float2 4.5s ease-in-out infinite; }
.ca-3 { top: 50%; right: -80px; transform: translateY(-50%); animation: float1 5s 0.5s ease-in-out infinite; }

/* ── FEATURES ─────────────────────────────────────────────────────────────── */
.flap-features { padding: 100px 5%; background: var(--offwhite); position: relative; }
.flap-features-inner { max-width: 1100px; margin: 0 auto; }
.section-label { display: block; font-size: 0.72rem; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--blue); margin-bottom: 12px; }
.section-heading { font-family: 'Clash Display', sans-serif; font-size: clamp(1.8rem, 3vw + 0.5rem, 2.8rem); font-weight: 700; color: var(--navy); line-height: 1.15; margin-bottom: 56px; }
.section-heading em { font-style: normal; color: var(--blue); }
.features-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
@media (max-width: 768px) {
  .features-grid { grid-template-columns: 1fr; }
  .flap-hero { grid-template-columns: 1fr; padding-top: 120px; }
  .hero-visual { display: none; }
  .hero-stats { gap: 20px; }
}
@media (max-width: 1024px) and (min-width: 769px) { .features-grid { grid-template-columns: 1fr 1fr; } }
.feature-item { background: #fff; border-radius: 18px; padding: 32px 28px; border: 1px solid rgba(28,115,186,0.1); transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s; position: relative; overflow: hidden; }
.feature-item::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, var(--blue), var(--blue3)); transform: scaleX(0); transform-origin: left; transition: transform 0.3s ease; }
.feature-item:hover { transform: translateY(-6px); box-shadow: 0 16px 40px rgba(28,115,186,0.12); border-color: rgba(28,115,186,0.2); }
.feature-item:hover::before { transform: scaleX(1); }
.feature-icon { width: 52px; height: 52px; border-radius: 14px; background: linear-gradient(135deg, rgba(28,115,186,0.1), rgba(28,115,186,0.06)); display: flex; align-items: center; justify-content: center; font-size: 1.5rem; margin-bottom: 20px; }
.feature-item h3 { font-family: 'Clash Display', sans-serif; font-size: 1.15rem; font-weight: 600; color: var(--navy); margin-bottom: 10px; }
.feature-item p { font-size: 0.9rem; color: #4b5563; line-height: 1.65; }

/* ── HOW IT WORKS ─────────────────────────────────────────────────────────── */
.flap-how { padding: 100px 5%; background: var(--navy); position: relative; overflow: hidden; }
.flap-how::before { content: ''; position: absolute; inset: 0; background-image: linear-gradient(rgba(28,115,186,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(28,115,186,0.05) 1px, transparent 1px); background-size: 48px 48px; }
.flap-how-inner { max-width: 1100px; margin: 0 auto; position: relative; }
.flap-how .section-label { color: var(--gold); }
.flap-how .section-heading { color: #fff; }
.flap-how .section-heading em { color: var(--blue3); }
.steps-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 24px; position: relative; }
.steps-row::before { content: ''; position: absolute; top: 32px; left: calc(12.5% + 16px); right: calc(12.5% + 16px); height: 2px; background: linear-gradient(90deg, var(--blue)44, var(--blue3)44); z-index: 0; }
@media (max-width: 768px) { .steps-row { grid-template-columns: 1fr 1fr; } .steps-row::before { display: none; } }
@media (max-width: 480px) { .steps-row { grid-template-columns: 1fr; } }
.step-item { text-align: center; position: relative; z-index: 1; opacity: 0; transform: translateY(24px); transition: opacity 0.6s ease, transform 0.6s ease; }
.step-item.visible { opacity: 1; transform: translateY(0); }
.step-num { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, var(--blue), var(--blue2)); color: #fff; font-family: 'Clash Display', sans-serif; font-size: 1.4rem; font-weight: 700; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px; box-shadow: 0 0 0 6px rgba(28,115,186,0.15); position: relative; }
.step-num::after { content: ''; position: absolute; inset: -6px; border-radius: 50%; border: 1px solid rgba(28,115,186,0.3); }
.step-item h4 { font-family: 'Clash Display', sans-serif; font-size: 1rem; font-weight: 600; color: #fff; margin-bottom: 8px; }
.step-item p { font-size: 0.83rem; color: var(--muted); line-height: 1.6; }

/* ── ACCESSORIES ─────────────────────────────────────────────────────────── */
.flap-accessories { padding: 100px 5%; background: var(--offwhite); }
.flap-accessories-inner { max-width: 1100px; margin: 0 auto; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
@media (max-width: 768px) { .flap-accessories-inner { grid-template-columns: 1fr; } }
.accessories-text .section-heading { margin-bottom: 20px; }
.accessories-text p { font-size: 0.95rem; color: #4b5563; line-height: 1.75; margin-bottom: 32px; }
.acc-chips { display: flex; gap: 10px; flex-wrap: wrap; }
.acc-chip { display: inline-flex; align-items: center; gap: 6px; padding: 7px 16px; border-radius: 99px; background: #fff; border: 1px solid rgba(28,115,186,0.2); font-size: 0.85rem; font-weight: 600; color: var(--navy); transition: all 0.2s; cursor: default; text-decoration: none; }
.acc-chip:hover { background: var(--blue); color: #fff; border-color: var(--blue); transform: translateY(-2px); }
.accessories-visual { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.acc-card { background: #fff; border-radius: 16px; padding: 28px 20px; text-align: center; border: 1px solid rgba(28,115,186,0.08); transition: transform 0.3s, box-shadow 0.3s; cursor: default; }
.acc-card:hover { transform: translateY(-4px); box-shadow: 0 12px 32px rgba(0,0,0,0.08); }
.acc-card-icon { font-size: 2.2rem; margin-bottom: 10px; }
.acc-card-name { font-family: 'Clash Display', sans-serif; font-size: 0.9rem; font-weight: 600; color: var(--navy); margin-bottom: 4px; }
.acc-card-desc { font-size: 0.78rem; color: var(--muted); }

/* ── CTA ──────────────────────────────────────────────────────────────────── */
.flap-cta { padding: 100px 5%; background: linear-gradient(135deg, var(--navy) 0%, var(--navy3) 60%, #1a3570 100%); text-align: center; position: relative; overflow: hidden; }
.flap-cta::before { content: ''; position: absolute; width: 800px; height: 800px; background: radial-gradient(circle, rgba(28,115,186,0.15) 0%, transparent 70%); top: 50%; left: 50%; transform: translate(-50%, -50%); }
.flap-cta h2 { font-family: 'Clash Display', sans-serif; font-size: clamp(1.8rem, 4vw, 3rem); font-weight: 700; color: #fff; position: relative; margin-bottom: 16px; }
.flap-cta h2 strong { background: linear-gradient(90deg, var(--gold), var(--gold2)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.flap-cta p { font-size: 1rem; color: rgba(255,255,255,0.6); margin-bottom: 36px; position: relative; }
.cta-btn-group { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; position: relative; }

/* ── ANIMATIONS ──────────────────────────────────────────────────────────── */
@keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
@keyframes cardFloat { 0%, 100% { transform: translateY(0px) rotateX(4deg) rotateY(-8deg); } 50% { transform: translateY(-14px) rotateX(4deg) rotateY(-8deg); } }
@keyframes cardTilt { 0%, 100% { transform: translateY(0px) rotateX(4deg) rotateY(-8deg); } 33% { transform: translateY(-7px) rotateX(6deg) rotateY(-12deg); } 66% { transform: translateY(-12px) rotateX(2deg) rotateY(-4deg); } }
@keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
@keyframes float1 { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
@keyframes float2 { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(8px); } }
@keyframes pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.5; transform: scale(0.8); } }
.reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
.reveal.visible { opacity: 1; transform: translateY(0); }
`;

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal, .step-item');
    const io = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.15 }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

function useNavScroll() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return scrolled;
}

// ── Live stats hook — fetches from public-stats endpoint ──────────────────
function useLiveStats() {
  const [stats, setStats] = useState(null); // null = loading
  useEffect(() => {
    fetch(`${API_BASE}/analytics/public-stats`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.ok) {
          setStats({
            cards_tapped:   data.display.cards_tapped,
            satisfaction:   '98',       // keep this one static — no data source for it
            products:       '4',        // number of NFC product types
          });
        }
      })
      .catch(() => {
        // Fallback to hardcoded if API is down
        setStats({ cards_tapped: '10K', satisfaction: '98', products: '4' });
      });
  }, []);
  return stats;
}

export default function Home() {
  const scrolled = useNavScroll();
  useReveal();
  const liveStats = useLiveStats();

  const FEATURES = [
    { icon:'🌱', title:'Eco-Friendly',      desc:'Replace disposable paper cards forever. Flap is reusable, sustainable, and built for the future.' },
    { icon:'⚡', title:'Instant Sharing',   desc:'Tap or scan—your full profile shared in under a second. No app install needed on the recipient side.' },
    { icon:'🎨', title:'Fully Customizable',desc:'Your profile, your brand. Add links, photos, socials, and update anytime in real time.' },
    { icon:'🏢', title:'Business Mode',     desc:'Run loyalty programs, attendance tracking and memberships—all through your existing Flap card.' },
    { icon:'🔒', title:'Privacy First',     desc:'You control what\'s visible. Toggle contact details, chat buttons and links on or off anytime.' },
    { icon:'📊', title:'Live Analytics',    desc:'See who viewed your profile, where they\'re from, and which links they clicked—in real time.' },
  ];

  const STEPS = [
    { num:'01', icon:'💳', title:'Get Your Flap',   desc:'Order a card, keyring, ring, or wallet online. Ships across Nepal.' },
    { num:'02', icon:'⚙️', title:'Set Up Profile',  desc:'Create your account, fill in your details, add all your links.' },
    { num:'03', icon:'📲', title:'Tap to Share',    desc:'Hold your accessory near any smartphone. Profile opens instantly.' },
    { num:'04', icon:'✨', title:'Stay Connected',  desc:'Contacts saved, messages sent, analytics tracking—all automatic.' },
  ];

  const ACCESSORIES = [
    { icon:'💳', name:'Smart Card',  desc:'Classic business card size' },
    { icon:'👜', name:'Wallet Tag', desc:'Clip inside any wallet' },
    { icon:'🔑', name:'Keyring',    desc:'Never leave home without it' },
    { icon:'💍', name:'NFC Ring',   desc:'The ultimate wearable' },
  ];

  const items = [
    { icon:'💳',     label:'Cards',       link:'/physical-card' },
    { icon:'👜',     label:'Wallets',     link:'/flap-wallet' },
    { icon:'💍🔑',  label:'Accessories', link:'/flap-accessories' },
    { icon:'QR',     label:'Dynamic QR',  link:'/dynamic-qr' },
    { icon:'🖥️',   label:'Stands',      link:'/flap-stand' },
  ];

  // Stats row: real data when loaded, shimmer skeleton while loading
  const statRows = [
    {
      num:    liveStats ? liveStats.cards_tapped : '···',
      suffix: liveStats ? '+' : '',
      label:  'Cards Tapped',
      loading: !liveStats,
    },
    {
      num:    liveStats ? liveStats.satisfaction : '···',
      suffix: liveStats ? '%' : '',
      label:  'Satisfaction',
      loading: !liveStats,
    },
    {
      num:    liveStats ? liveStats.products : '···',
      suffix: liveStats ? 'x' : '',
      label:  'NFC Products',
      loading: !liveStats,
    },
  ];

  return (
    <div className="flap-home">
      <style>{STYLES}</style>

      {/* ── NAV ── */}
      <nav className={`flap-nav ${scrolled ? 'scrolled' : ''}`}>
        <Link to="/" className="flap-nav-logo">flap<span>.</span></Link>
        <div className="flap-nav-links">
          <a href="#features" className="hide-mobile">Features</a>
          <a href="#how"      className="hide-mobile">How it works</a>
          <a href="#accessories" className="hide-mobile">Products</a>
          <Link to="/login">Login</Link>
          <Link to="/register" className="flap-nav-cta">Get Started</Link>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="flap-hero">
        <div className="hero-glow2" />
        <div className="flap-hero-text">
          <div className="flap-hero-eyebrow">
            <span className="eyebrow-dot" />
            Nepal's #1 Digital Card Platform
          </div>
          <h1>
            Share your world<br />
            with a single <em>tap</em><br />
            <strong>No paper. Ever.</strong>
          </h1>
          <p className="flap-hero-sub">
            Connect your card, ring, wallet, or keyring and share your entire
            profile instantly via NFC or QR — across Nepal and beyond.
          </p>
          <div className="hero-btns">
            <Link to="/register" className="hero-btn-primary">Create Free Account →</Link>
            <Link to="/login"    className="hero-btn-secondary">Sign In</Link>
          </div>

          {/* ── LIVE STATS ── */}
          <div className="hero-stats">
            {statRows.map(s => (
              <div key={s.label}>
                <div className={`hero-stat-num ${s.loading ? 'loading' : ''}`}>
                  {s.loading ? '···' : s.num}<span>{s.suffix}</span>
                </div>
                <div className="hero-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Floating NFC Card */}
        <div className="hero-visual">
          <div style={{ position:'relative' }}>
            <div className="card-accessory ca-1">💍 Ring · NFC</div>
            <div className="card-accessory ca-2">🔑 Keyring · Active</div>
            <div className="card-accessory ca-3">📲 Just tapped!</div>
            <div className="card-scene">
              <div className="card-3d">
                <div className="card-face">
                  <div className="card-logo">flap<span>.</span></div>
                  <div className="card-chip" />
                  <div className="card-name">
                    Your Name Here
                    <div className="card-detail">Digital Business Card</div>
                  </div>
                  <div className="card-nfc">
                    <div className="card-nfc-rings"><span /></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURES ── */}
      <section className="flap-features" id="features">
        <div className="flap-features-inner">
          <span className="section-label reveal">Why Flap</span>
          <h2 className="section-heading reveal">Everything you need to<br /><em>network smarter</em></h2>
          <div className="features-grid">
            {FEATURES.map((f, i) => (
              <div className="feature-item reveal" key={f.title} style={{ transitionDelay: `${i * 80}ms` }}>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section className="flap-how" id="how">
        <div className="flap-how-inner">
          <span className="section-label">Simple Setup</span>
          <h2 className="section-heading">Up and running in<br /><em>4 easy steps</em></h2>
          <div className="steps-row">
            {STEPS.map((s, i) => (
              <div className="step-item" key={s.num} style={{ transitionDelay: `${i * 120}ms` }}>
                <div className="step-num">{s.icon}</div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── ACCESSORIES ── */}
      <section className="flap-accessories" id="accessories">
        <div className="flap-accessories-inner">
          <div className="accessories-text reveal">
            <span className="section-label">NFC Products</span>
            <h2 className="section-heading">One profile.<br /><em>Different ways to share.</em></h2>
            <p>
              Every Flap accessory is linked to the same profile. Update once,
              share everywhere. Mix and match — carry a card, wear a ring,
              clip on a keyring. Your digital identity travels with you.
            </p>
            <div>
              {items.map(item => (
                <Link to={item.link} key={item.label} className="acc-chip">
                  {item.icon} {item.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="accessories-visual reveal" style={{ transitionDelay:'100ms' }}>
            {ACCESSORIES.map(a => (
              <div className="acc-card" key={a.name}>
                <div className="acc-card-icon">{a.icon}</div>
                <div className="acc-card-name">{a.name}</div>
                <div className="acc-card-desc">{a.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <div className="reveal"><Testimonial1 /></div>

      {/* ── LIVE MAP ── */}
      <div className="reveal"><Flapinaction /></div>

      {/* ── TRUSTED BY ── */}
      <div className="reveal"><TrustedBy /></div>

      {/* ── ABOUT ── */}
      <div className="reveal"><About /></div>

      <Footer />
    </div>
  );
}